import {
  AgentChatRunId,
  AgentChatThreadId,
  AiBillingUsageId,
  DateTimeString,
  HexVersionId,
  UserId,
  stableEmptyArray,
} from "@hex/common";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { compareAsc, parseISO } from "date-fns";
import { groupBy, sortBy } from "lodash";

import { AgentChatMessageFragment } from "../../components/agent/hex-project-shared/messages/AgentChatMessage.generated";
import {
  AgentChatMessage,
  AgentChatThread,
  AgentCreditSpend,
} from "../../components/agent/shared/agentChatMessage.types";
import {
  messageFragmentToAgentChatMessage,
  threadFragmentToAgentChatThread,
} from "../../components/agent/shared/agentChatMessageTypeUtils";
import {
  AgentChatThreadFragment,
  AgentChatThreadListItemFragment,
} from "../../components/agent/shared/sharedThreadManagementUtils.generated";
import { AgentChatThreadStatus } from "../../generated/graphqlTypes";
import { RootState } from "../store";
import { createShallowResultSelector } from "../utils/index.js";

type AgentChatState = {
  /**
   * A lightweight list of agent chat threads without messages or edits.
   */
  agentChatThreadsMap: Record<AgentChatThreadId, AgentChatThread>;
  /**
   * True when the initial page of agent chat threads is loading.
   */
  isLoadingAgentChatThreads: boolean;
  /**
   * True when there are more threads that can be loaded via a paginated query.
   */
  hasMoreThreads: boolean;
  /**
   * True when the next page of threads for the thread list is loading.
   */
  isLoadingMoreChatThreads: boolean;
  /**
   * The id of the currently selected thread.
   */
  selectedThreadId: AgentChatThreadId | null;
  /**
   * True when the full details of the selected thread are loading.
   */
  isLoadingSelectedThread: boolean;
  /**
   * Incremented to trigger loading more threads. Components can dispatch
   * triggerLoadMoreThreads to increment this value and useAgentChatThreads
   * will respond by loading the next page.
   */
  loadMoreThreadsTrigger: number;
  /**
   * A record of all received credit spend events.
   */
  agentCreditSpend: Record<AiBillingUsageId, AgentCreditSpend>;
};

const initialState: AgentChatState = {
  agentChatThreadsMap: {},
  isLoadingAgentChatThreads: false,
  hasMoreThreads: false,
  isLoadingMoreChatThreads: false,
  selectedThreadId: null,
  isLoadingSelectedThread: false,
  loadMoreThreadsTrigger: 0,
  agentCreditSpend: {},
};

/**
 * Manages agent chat thread state including thread list, selection, and messages.
 */
export const agentChatSlice = createSlice({
  name: "agentChat",
  initialState,
  reducers: {
    resetForNewHex: (
      _state: AgentChatState,
      action: PayloadAction<{ selectedThreadId: AgentChatThreadId | null }>,
    ) => {
      // Reset all state for new hex, but preserve the stored thread selection
      return {
        ...initialState,
        selectedThreadId: action.payload.selectedThreadId,
      };
    },
    setAgentChatThreads: (
      state: AgentChatState,
      action: PayloadAction<readonly AgentChatThreadListItemFragment[]>,
    ) => {
      const threads: AgentChatThread[] = action.payload.map(
        threadFragmentToAgentChatThread,
      );

      // Reset and rebuild the threads map
      state.agentChatThreadsMap = {};

      for (const thread of threads) {
        state.agentChatThreadsMap[thread.id] = thread;
      }
    },
    appendAgentChatThreads: (
      state: AgentChatState,
      action: PayloadAction<readonly AgentChatThreadListItemFragment[]>,
    ) => {
      const threads: AgentChatThread[] = action.payload.map(
        threadFragmentToAgentChatThread,
      );

      for (const thread of threads) {
        state.agentChatThreadsMap[thread.id] = thread;
      }
    },
    setHasMoreThreads: (
      state: AgentChatState,
      action: PayloadAction<boolean>,
    ) => {
      state.hasMoreThreads = action.payload;
    },
    setIsLoadingAgentChatThreads: (
      state: AgentChatState,
      action: PayloadAction<boolean>,
    ) => {
      state.isLoadingAgentChatThreads = action.payload;
    },
    setIsLoadingMoreChatThreads: (
      state: AgentChatState,
      action: PayloadAction<boolean>,
    ) => {
      state.isLoadingMoreChatThreads = action.payload;
    },
    triggerLoadMoreThreads: (state: AgentChatState) => {
      state.loadMoreThreadsTrigger = state.loadMoreThreadsTrigger + 1;
    },
    setSelectedThreadId: (
      state: AgentChatState,
      action: PayloadAction<AgentChatThreadId | null>,
    ) => {
      if (action.payload === state.selectedThreadId) {
        return;
      }

      state.selectedThreadId = action.payload;
      // Nothing to load if the thread id is null
      state.isLoadingSelectedThread = action.payload != null;
    },
    /**
     * Sets messages for a thread.
     * Subagent messages are now stored in the same thread with subagentType set on the message.
     */
    setMessagesForThread: (
      state: AgentChatState,
      action: PayloadAction<{
        thread: AgentChatThreadFragment;
        currentUserId: UserId | null;
      }>,
    ) => {
      const { currentUserId, thread } = action.payload;

      const messages = thread.agentMessages.edges.map((e) => e.node);
      setThreadMessages(state, {
        threadId: thread.id,
        messages,
        currentUserId,
      });

      // Carry the server-side truncation signal through so the UI can render
      // an "earlier messages hidden" banner. False when the full thread was
      // returned (the common case).
      const storedThread = state.agentChatThreadsMap[thread.id];
      if (storedThread != null) {
        storedThread.earlierMessagesHidden =
          thread.agentMessages.pageInfo?.hasPreviousPage ?? false;
      }

      state.isLoadingSelectedThread = false;
    },
    setIsLoadingSelectedThread: (
      state: AgentChatState,
      action: PayloadAction<boolean>,
    ) => {
      state.isLoadingSelectedThread = action.payload;
    },
    addMessagesToThread: (
      state: AgentChatState,
      action: PayloadAction<{
        threadId: AgentChatThreadId;
        messages: readonly AgentChatMessageFragment[];
        currentUserId: UserId | null;
      }>,
    ) => {
      const { currentUserId, messages, threadId } = action.payload;

      const thread = ensureThreadExists(state, threadId, currentUserId);

      // Check if these are subagent messages (have subagentType set)
      const hasSubagentMessages = messages.some((m) => m.subagentType != null);

      // Skip adding messages if user explicitly cancelled (IDLE_STOP_REQUESTED)
      // This prevents lagging messages from appearing after user clicks stop.
      // Normal IDLE (agent completed naturally) should still accept messages.
      // BUT allow subagent messages to continue streaming even after
      // the thread status becomes IDLE_STOP_REQUESTED, since the parent thread may still be active
      const existingThread = state.agentChatThreadsMap[threadId];
      if (
        existingThread?.status === "IDLE_STOP_REQUESTED" &&
        !hasSubagentMessages
      ) {
        return;
      }

      const threadCopy = { ...thread };
      const newMessages: AgentChatMessage[] = messages.map(
        messageFragmentToAgentChatMessage,
      );

      // Combine existing messages and new messages, deduplicating by staticId
      threadCopy.agentMessages = mergeMessagesWithRevisions(
        threadCopy.agentMessages,
        newMessages,
      );

      state.agentChatThreadsMap[threadId] = threadCopy;
    },
    updateThreadMetadata: (
      state: AgentChatState,
      action: PayloadAction<{
        threadId: AgentChatThreadId;
        updates: {
          title: string | null;
          status: AgentChatThreadStatus;
          autoSaveHexVersionId: HexVersionId | null;
        };
        currentUserId: UserId | null;
      }>,
    ) => {
      const { currentUserId, threadId, updates } = action.payload;
      const thread = ensureThreadExists(state, threadId, currentUserId);

      // Make IDLE_STOP_REQUESTED "sticky" - if user stopped the thread, ignore
      // incoming IDLE from workflow completion to prevent lagging messages from
      // being accepted. Any other status (RUNNING, SAVING_VERSION) can overwrite.
      const existingStatus = thread.status;
      const newStatus =
        existingStatus === "IDLE_STOP_REQUESTED" && updates.status === "IDLE"
          ? "IDLE_STOP_REQUESTED"
          : updates.status;

      const threadCopy = { ...thread, status: newStatus };

      if (updates.title) {
        threadCopy.title = updates.title;
      }

      if (updates.autoSaveHexVersionId) {
        threadCopy.autoSaveHexVersionId = updates.autoSaveHexVersionId;
      }

      state.agentChatThreadsMap[threadId] = threadCopy;
    },
    updateThreadIsSensitive: (
      state: AgentChatState,
      action: PayloadAction<{
        threadId: AgentChatThreadId;
        sensitive: boolean;
      }>,
    ) => {
      const { sensitive, threadId } = action.payload;
      const thread = state.agentChatThreadsMap?.[threadId];
      if (!thread) return;
      thread.sensitive = sensitive;
    },
    removeThread: (
      state: AgentChatState,
      action: PayloadAction<{ threadId: AgentChatThreadId }>,
    ) => {
      const { threadId } = action.payload;
      delete state.agentChatThreadsMap[threadId];
    },
    addCreditSpend: (
      state: AgentChatState,
      action: PayloadAction<{
        id: AiBillingUsageId;
        runId: AgentChatRunId;
        millionthCreditCost: number;
      }>,
    ) => {
      state.agentCreditSpend[action.payload.id] = action.payload;
    },
  },
});

const selectThreads = createShallowResultSelector(
  [(state: RootState) => state.agentChat.agentChatThreadsMap],
  (threadsMap) => sortBy(Object.values(threadsMap), "createdDate").reverse(),
);

/**
 * Returns a thread's messages sorted chronologically by createdDate.
 * Subagent messages are now stored in the same thread with subagentType set on the message.
 */
const selectThreadMessages = (
  state: RootState,
  threadId: AgentChatThreadId | null,
): readonly AgentChatMessage[] => {
  if (!threadId) return stableEmptyArray<AgentChatMessage>();

  const thread = state.agentChat.agentChatThreadsMap[threadId];
  if (!thread) return stableEmptyArray<AgentChatMessage>();

  return thread.agentMessages;
};

export const agentChatSelectors = {
  selectIsLoadingThreads: (state: RootState) =>
    state.agentChat.isLoadingAgentChatThreads,
  selectIsLoadingSelectedThread: (state: RootState) =>
    state.agentChat.isLoadingSelectedThread,
  selectSelectedThreadId: (state: RootState) =>
    state.agentChat.selectedThreadId,
  selectIsLoadingMoreChatThreads: (state: RootState) =>
    state.agentChat.isLoadingMoreChatThreads,
  selectHasMoreThreads: (state: RootState) => state.agentChat.hasMoreThreads,
  selectLoadMoreThreadsTrigger: (state: RootState) =>
    state.agentChat.loadMoreThreadsTrigger,

  selectThreads,
  selectHasThreads: (state: RootState) => selectThreads(state).length > 0,

  selectSelectedThread: (state: RootState) => {
    const selectedThreadId = state.agentChat.selectedThreadId;
    return selectedThreadId
      ? state.agentChat.agentChatThreadsMap[selectedThreadId] || null
      : null;
  },

  /**
   * Returns the selected thread's messages.
   * Subagent messages are included in the thread with subagentType set on the message.
   */
  selectSelectedThreadMessages: (state: RootState) => {
    const selectedThreadId = state.agentChat.selectedThreadId;
    return selectThreadMessages(state, selectedThreadId);
  },

  selectCreditSpends: (state: RootState) => state.agentChat.agentCreditSpend,
};

export const agentChatReducer = agentChatSlice.reducer;

export const {
  addCreditSpend,
  addMessagesToThread,
  appendAgentChatThreads,
  removeThread,
  resetForNewHex,
  setAgentChatThreads,
  setHasMoreThreads,
  setIsLoadingAgentChatThreads,
  setIsLoadingMoreChatThreads,
  setIsLoadingSelectedThread,
  setMessagesForThread,
  setSelectedThreadId,
  triggerLoadMoreThreads,
  updateThreadIsSensitive,
  updateThreadMetadata,
} = agentChatSlice.actions;

/**
 * Creates an optimistic thread when we receive updates for a thread that
 * doesn't exist locally. This can happen when we get real-time updates before
 * the thread list has been loaded.
 *
 * NOTE: This function assumes that any optimistically created threads belong to
 * the currently logged-in user.
 */
const createOptimisticThread = (
  threadId: AgentChatThreadId,
  currentUserId: UserId | null,
): AgentChatThread => {
  return {
    id: threadId,
    hexId: null,
    hexVersionId: null,
    title: null,
    createdDate: new Date().toISOString() as DateTimeString,
    status: "IDLE" as AgentChatThreadStatus,
    autoSaveHexVersionId: null,
    agentMessages: [],
    editsToReview: [],
    userId: currentUserId,
    lastUsedEffortLevel: null,
  };
};

/**
 * Ensures a thread exists in the state, creating it optimistically if needed.
 * Returns the thread (existing or newly created).
 */
const ensureThreadExists = (
  state: AgentChatState,
  threadId: AgentChatThreadId,
  currentUserId: UserId | null,
): AgentChatThread => {
  let thread = state.agentChatThreadsMap[threadId];

  if (!thread) {
    thread = createOptimisticThread(threadId, currentUserId);
    state.agentChatThreadsMap[threadId] = thread;
  }

  return thread;
};

/**
 * Sets messages for a thread in state.
 * Deduplicates messages by staticId, keeping the most recent for streaming updates.
 */
function setThreadMessages(
  state: AgentChatState,
  {
    currentUserId,
    messages,
    threadId,
  }: {
    threadId: AgentChatThreadId;
    messages: readonly AgentChatMessageFragment[];
    currentUserId: UserId | null;
  },
): void {
  const thread = ensureThreadExists(state, threadId, currentUserId);
  const copy = { ...thread };

  const newMessages = messages.map(messageFragmentToAgentChatMessage);
  copy.agentMessages = mergeMessagesWithRevisions(
    copy.agentMessages,
    newMessages,
  );

  state.agentChatThreadsMap[threadId] = copy;
}

/**
 * Merges existing messages with new messages, deduplicating by staticId.
 * For messages with the same staticId, keeps the one with the latest createdDate.
 * This handles streaming previews being replaced by newer updates or persisted messages.
 */
function mergeMessagesWithRevisions(
  existingMessages: readonly AgentChatMessage[],
  newMessages: readonly AgentChatMessage[],
): AgentChatMessage[] {
  const allMessages = [...existingMessages, ...newMessages];
  const messagesByStaticId = groupBy(allMessages, "staticId");

  return Object.values(messagesByStaticId)
    .map((messagesWithSameStaticId) => {
      // For messages with the same staticId, keep the one with the latest createdDate
      return messagesWithSameStaticId.reduce((latest, current) => {
        const latestDate = parseISO(latest.createdDate);
        const currentDate = parseISO(current.createdDate);
        return compareAsc(currentDate, latestDate) >= 0 ? current : latest;
      });
    })
    .sort((a, b) =>
      compareAsc(parseISO(a.createdDate), parseISO(b.createdDate)),
    );
}
